import uniq from 'lodash-es/uniq';
import { IResourceVersion, ISchemaField } from '@/types';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import {
  TAssociation,
  TCompareDefinition,
  TAssociationFieldSelection,
  ICompareSchemaField,
} from '@/lib/tabularCompareApi';

const generateAssociations = (leftFields: ISchemaField[], rightFields: ISchemaField[]): TAssociation[] => {
  return leftFields
    .map((field) => {
      if (rightFields.some((f) => f.name === field.name)) {
        const rightField = rightFields.find((f) => f.name === field.name) as ISchemaField;
        const dataTypeMatch = rightField.dtype === field.dtype;
        return dataTypeMatch
          ? ({
              isKey: false,
              left: field,
              right: rightField,
            } as TAssociation)
          : null;
      }
    })
    .filter((a) => a) as TAssociation[];
};

const compileAssociations = (
  associations: TAssociationFieldSelection[],
  leftFields: ISchemaField[],
  rightFields: ISchemaField[],
  isKey: boolean,
  isTarget: boolean,
): TAssociation[] => {
  return associations
    .map((association) => {
      const leftField = leftFields.find((f) => f.name === association.left) as ISchemaField;
      const rightField = rightFields.find((f) => f.name === association.right) as ISchemaField;
      // handle case where field(s) are removed from schema compare
      if (!leftField || !rightField) {
        return {
          isKey: isKey,
          isTarget: isTarget,
          left: { name: association.left, elementId: 'left.' + association.left },
          right: { name: association.right, elementId: 'right.' + association.left },
        };
      }
      return {
        isKey: isKey,
        isTarget: isTarget,
        left: leftField,
        right: rightField,
      };
    })
    .map((a) => a as TAssociation);
};

const initializeAssociationFromDefinition = (
  leftFields: ISchemaField[],
  rightFields: ISchemaField[],
  definition: TCompareDefinition,
): TAssociation[] => {
  const { key, compare, display } = definition.fields;
  const keyOutput = compileAssociations(key, leftFields, rightFields, true, false);
  const compareOutput = compileAssociations(compare, leftFields, rightFields, false, true);

  // This is a hack to get this all working
  let displayCleaned = display.map((d) => {
    let name = d.left || d.right;
    name = name?.split('.')[0];
    return { name: name, left: name, right: name };
  });

  // create unique list of display fields
  displayCleaned = displayCleaned.filter((v, i, a) => a.findIndex((t) => t.name === v.name) === i);

  const displayOutput = compileAssociations(displayCleaned, leftFields, rightFields, false, false);

  return [...keyOutput, ...compareOutput, ...displayOutput].filter(function (x) {
    return !!x;
  });
};

export const initializeAssociations = (
  leftFields: ISchemaField[],
  rightFields: ISchemaField[],
  definition: TCompareDefinition,
) => {
  return definition
    ? initializeAssociationFromDefinition(leftFields, rightFields, definition)
    : generateAssociations(leftFields, rightFields);
};

export const compileCompareDefinition = (
  associations: TAssociation[],
  unLinkedFields: ICompareSchemaField[],
  leftSourceResource: IResourceVersion,
  rightSourceResource: IResourceVersion,
  title?: string,
  description?: string,
): TCompareDefinition => {
  const emptyResource = { path: 'PATH_TO_FILE', version: 'COMMIT_REF_OR_BRANCH_NAME' };

  return {
    data_frames: {
      left: leftSourceResource || emptyResource,
      right: rightSourceResource || emptyResource,
    },
    fields: {
      key: associations
        .filter((a) => a.isKey)
        .map((a) => ({
          left: a.left.name,
          right: a.right.name,
        })),
      compare: associations
        .filter((a) => a.isTarget)
        .map((a) => ({
          left: a.left.name,
          right: a.right.name,
          alias_as: null,
          compare_method: null,
        })),
      display: [],
    },
    title: title,
    description: description,
  };
};

export const getMergedFields = (leftSchema: IDataFrameSchema, rightSchema: IDataFrameSchema) => {
  const leftFields = leftSchema.fields.map((field) => ({
    name: field.name,
    left: field,
  }));
  const rightFields = rightSchema.fields.map((field) => ({
    name: field.name,
    right: field,
  }));

  const fieldNames = uniq([leftFields, rightFields].flatMap((field) => field.map((x) => x.name)));
  const fields = fieldNames.map((name) => {
    const leftField = leftFields.find((field) => field.name === name);
    const rightField = rightFields.find((field) => field.name === name);
    return { ...leftField, ...rightField } as ICompareSchemaField;
  });
  return fields;
};

export const initializeSelections = (mergedFields: ICompareSchemaField[], definition: TCompareDefinition) => {
  return mergedFields.filter((field) => {
    const { key, compare, display } = definition.fields;
    const initialKeyFields = key.some((f) => f.left == field.name || f.right == field.name);
    const initialCompareFields = compare.some((f) => f.left == field.name || f.right == field.name);
    const initialDisplayFields = display.some((f) => f.left == field.name || f.right == field.name);
    return initialKeyFields || initialCompareFields || initialDisplayFields;
  });
};

export const getUnLinkedFields = (
  leftFields: ICompareSchemaField[],
  rightFields: ICompareSchemaField[],
  associations: TAssociation[],
) => {
  const unlinkedLeftFields = leftFields.filter((f) => !associations.some((a) => a.left?.elementId === f.elementId));
  const unlinkedRightFields = rightFields.filter((f) => !associations.some((a) => a.right?.elementId === f.elementId));
  return [...unlinkedLeftFields, ...unlinkedRightFields];
};

export const getLeftFields = (schemaLeft: IDataFrameSchema, selectedFields: ICompareSchemaField[]) => {
  return schemaLeft.fields
    .filter((field) => selectedFields.some((x) => x.name == field.name))
    .map((x) => ({ ...x, elementId: `left.${x.name}` }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const getRightFields = (schemaRight: IDataFrameSchema, selectedFields: ICompareSchemaField[]) => {
  return schemaRight.fields
    .filter((field) => selectedFields.some((x) => x.name == field.name))
    .map((x) => ({ ...x, elementId: `right.${x.name}` }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
